import { createI18n } from 'vue-i18n'
import en from '@/locales/en.json'
import { PAGE_LANGUAGE, currentLanguage, setLanguage } from '@/globals/google-translate'

/*
 * Real i18n, alongside the Google Translate widget rather than instead of it.
 *
 * WHY BOTH, FOR NOW
 * The switcher offers 15 languages. Google machine-translates all of them for
 * free and stays current as the UI changes; hand-written catalogues do neither.
 * Deleting the widget the moment this file existed would have handed every
 * non-English user an English panel — a regression dressed as a cleanup.
 *
 * So the order is: catalogue first, translations second, widget last. Until a
 * locale has a real catalogue, `fallbackLocale` serves English and Google
 * translates it exactly as it does today. As catalogues land, those strings
 * stop being machine-translated and start being ours, one locale at a time,
 * with nothing switching over in a single step.
 *
 * WHO OWNS THE LOCALE — google-translate.js, not this file.
 * It already stores the choice (the `googtrans` cookie, shared with the
 * marketing site), writes <html lang/dir>, toggles the `.rtl` class the
 * stylesheets key off, and fires the `resize` that makes App.vue move the
 * sidebar's body padding to the other side. This module deliberately does NOT
 * do any of that.
 *
 * An earlier version of this file kept its own `ui-locale` in localStorage and
 * set lang/dir itself. It did not work: `initGoogleTranslate()` runs later and
 * re-applies from the cookie, so selecting Arabic left the document `ltr` —
 * verified in the browser. Two owners of one setting is the bug, and the fix is
 * to have one. Everything here reads through `currentLanguage()` and writes
 * through `setLanguage()`.
 */

// Locales with a hand-written, reviewed catalogue in src/locales/<code>.json.
// Add a code here only when that file exists AND someone who reads the language
// has checked it — this list is what tells the UI which languages are really
// translated rather than machine-read.
export const AVAILABLE_LOCALES = ['en']

export const hasCatalogue = (code) => AVAILABLE_LOCALES.includes(code)

export const i18n = createI18n({
  // Composition API mode. `legacy: true` injects $t through a mixin and breaks
  // `<script setup>` components, which is all of them here.
  legacy: false,
  globalInjection: true,
  // Seeded from the cookie so the first render already agrees with <html lang>,
  // which google-translate.js sets from the same source.
  locale: currentLanguage(),
  fallbackLocale: PAGE_LANGUAGE,
  messages: { en },
  // A key with no catalogue entry is not worth a console wall while the
  // catalogue is still being filled in — the fallback already covers the user.
  missingWarn: false,
  fallbackWarn: false,
})

/*
 * Change the UI language. Delegates the storage, the document attributes and
 * the re-layout to the module that already owns them, and only adds the part
 * that is genuinely new: pointing vue-i18n at the same locale.
 *
 * Note `setLanguage` may reload the page (switching back to English, or when
 * the widget has not loaded). That is fine — `locale` is seeded from the cookie
 * on boot, so the value survives.
 */
export const setLocale = (code) => {
  i18n.global.locale.value = code
  setLanguage(code)
}

export default i18n
